import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Switch,
} from "@mui/material";
import { FixedSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import wrappedFaCategories from "./faCategories";
import iconLibraryCategories from "./iconLibrary";
import { humanizeIconName, iconSearchText } from "./iconSearch";
import Tabs from "../../Tabs";
import ICON_CATEGORY_TAB_PROPS from "./iconCategoryTabProps";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomAccordion from "../../Accordion";
import {
  ICON_NO_ICON_TILE_ENABLED,
  ICON_LIBRARY_V2_ENABLED,
  ICON_SUBJECT_CATEGORIES_ENABLED,
} from "../../../constants/featureFlags";
import { groupCategoryMapBySubject } from "../../SegmentBuilder/Shared/SegmentStyling/logic/iconSubjects";

// Brand purple token (design-handoff). Nemesis F8: the picker chrome previously
// used three off-brand purples (#81459B tab text, #C283DA indicator, #6F27D6
// none-tile). All are unified to the single brand token here.
const BRAND_PURPLE = "#9B5AF8";
// One source of truth for the grid geometry. Nemesis F6: the flat cell index and
// rowCount MUST divide by the SAME column count (previously columnCount was 9 but
// rowCount divided by 8, over-provisioning ~one spurious blank row).
const COLUMN_COUNT = 9;

const SegmentIcon = ({
  selectedIcon,
  onIconSelect,
  title = "Segment Icon",
  showAccordion = true,
  // Segment-icon-picker (2026-08-18): the old header (x) clear button WIPED the
  // stored icon, forcing the user to scroll back and re-find it. It is replaced by
  // a persisted show/hide TOGGLE. `hidden` (from segment.settings.iconHidden) OFF =
  // icon shown, ON = hidden on the segment WITHOUT clearing the stored icon name;
  // `onToggleHidden(nextHidden)` writes it. Both optional so the showAccordion=false
  // callers (ListTab) are unaffected.
  hidden = false,
  onToggleHidden,
}) => {
  // Flag-gated icon set. When OFF (default) the picker consumes the legacy
  // `faCategories` map exactly as today (byte-identical). When ON it consumes the
  // stocked line-art library (deck concept 04-icon-library / Decision #35). The
  // flag is a build-time constant, so `categories` is stable across renders and
  // safe to read from the useState initializer below.
  const baseCategories = ICON_LIBRARY_V2_ENABLED
    ? iconLibraryCategories
    : wrappedFaCategories;

  // DL-045: re-key the SAME icon map by SUBJECT instead of by the pack's style
  // family. Swapping the map is the entire integration — every tab, grid and
  // search computation below is untouched, so the flag-OFF path stays
  // byte-identical. Both flags are build-time constants, so this is stable
  // across renders and safe to read from the useState initializer below.
  const categories = ICON_SUBJECT_CATEGORIES_ENABLED
    ? groupCategoryMapBySubject(baseCategories)
    : baseCategories;

  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(
    Object.keys(categories)[0]
  );


  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const icons = categories[selectedCategory];

  // Nemesis F4: match human labels + synonyms, not just the raw Tb*/Fi* code
  // name. `iconSearchText` is a SUPERSET (still contains the raw displayName), so
  // every query that matched before still matches — non-regressive for the legacy
  // faCategories set.
  const normalizedTerm = searchTerm.trim().toLowerCase();
  // Nemesis P15 (Y1): search was scoped to the CURRENT tab, so a term living in
  // another category (e.g. "pizza" from the default Essentials tab) fell through
  // to "No icons found". When a term is present, search across ALL categories
  // (deduped by displayName); with no term, show just the selected category as
  // before. Non-regressive: an in-category match is still in the global pool.
  const searchPool = normalizedTerm
    ? Array.from(
        new Map(
          Object.values(categories)
            .flat()
            .map((IconComponent) => [IconComponent.displayName, IconComponent])
        ).values()
      )
    : icons;
  const filteredIcons = searchPool.filter((IconComponent) =>
    iconSearchText(IconComponent.displayName).includes(normalizedTerm)
  );

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    // Nemesis F5: a stale search from the previous category could silently show
    // an empty tab. Reset the search when the category changes.
    setSearchTerm("");
  };

  const handleIconSelect = (IconComponent) => {
    onIconSelect(IconComponent ? <IconComponent /> : "");
  };

  // Flag-gated "No icon" tile. When OFF (default) noneTileOffset is 0 and every
  // grid computation below is byte-identical to today. When ON, one synthetic
  // dashed tile is prepended at flat index 0 and the real icons shift by one.
  const showNoneTile = ICON_NO_ICON_TILE_ENABLED;
  const noneTileOffset = showNoneTile ? 1 : 0;
  const isNoneSelected =
    selectedIcon === "" ||
    selectedIcon === null ||
    selectedIcon === undefined;

  const content = (
    <Box>
      {/* T22.90 (Kelly 2026-10-10: "once you choose an icon, there's no way to
          un-choose it"): a plain, labelled way to take this segment's icon off,
          shown only while it has one. Same clear path as the "No icon" tile. */}
      {!isNoneSelected && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: "5px", pt: 1 }}>
          <button
            type="button"
            onClick={() => handleIconSelect(null)}
            data-testid="segment-icon-remove"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              minHeight: 32,
              padding: "4px 10px",
              border: `1px solid ${BRAND_PURPLE}`,
              borderRadius: 6,
              background: "#FFFFFF",
              color: BRAND_PURPLE,
              fontFamily: "DM Sans",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
            Remove icon
          </button>
        </Box>
      )}
      <Tabs
        categories={Object.keys(categories)}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        color={BRAND_PURPLE}
        indicatorColor={BRAND_PURPLE}
        // 21 categories in a 354px track: fullWidth slices them to 24px each,
        // clipping 20 of 21 labels and pushing the last four past the viewport
        // where nothing can reach them. See iconCategoryTabProps.js for the
        // measured numbers.
        {...ICON_CATEGORY_TAB_PROPS}
      />
      <Box sx={{ padding: "10px 5px" }}>
        <TextField
          label="Search icon"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: "#F9FAFB" }}
        />
      </Box>
      <Box
        style={{
          height: "200px",
          backgroundColor: "#F9FAFB",
        }}
      >
        {/* Nemesis F5: a no-match search rendered an empty grey box. Show an
            explicit "No icons found" state instead. */}
        {filteredIcons.length === 0 ? (
          <Box
            role="status"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#667085",
              fontFamily: "DM Sans",
              fontSize: 14,
              textAlign: "center",
              px: 2,
            }}
          >
            No icons found{searchTerm.trim() ? ` for "${searchTerm.trim()}"` : ""}
          </Box>
        ) : (
          <AutoSizer>
            {({ height, width }) => (
              <Grid
                columnCount={COLUMN_COUNT}
                columnWidth={width / COLUMN_COUNT}
                height={height}
                rowCount={Math.ceil(
                  (filteredIcons.length + noneTileOffset) / COLUMN_COUNT
                )}
                rowHeight={50}
                width={width}
                style={{
                  overflowX: "hidden",
                  scrollbarWidth: "none", // For Firefox
                  msOverflowStyle: "none",
                }}
              >
                {({ columnIndex, rowIndex, style }) => {
                  const index = rowIndex * COLUMN_COUNT + columnIndex;
                  if (showNoneTile && index === 0) {
                    return (
                      <div style={style} key="none-tile">
                        <div
                          role="button"
                          tabIndex={0}
                          aria-label="No icon"
                          aria-pressed={isNoneSelected}
                          title="No icon"
                          onClick={() => handleIconSelect(null)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleIconSelect(null);
                            }
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "86%",
                            aspectRatio: "1 / 1",
                            maxWidth: 34,
                            maxHeight: 34,
                            margin: "6px auto 0",
                            borderRadius: 6,
                            border: isNoneSelected
                              ? `1.5px solid ${BRAND_PURPLE}`
                              : "1.5px dashed #B0B7C3",
                            color: isNoneSelected ? BRAND_PURPLE : "#667085",
                            cursor: "pointer",
                            boxSizing: "border-box",
                          }}
                        >
                          <BlockIcon style={{ fontSize: 18 }} />
                        </div>
                      </div>
                    );
                  }
                  const IconComponent = filteredIcons[index - noneTileOffset];
                  if (!IconComponent) {
                    return <div style={style} key={index} />;
                  }
                  // Nemesis F3 (G-20): each tile must be a real focusable control
                  // with an accessible name + keyboard activation — not a bare
                  // <svg onClick>. A native <button> gives Enter/Space activation,
                  // tab focus and a visible focus ring for free.
                  const label =
                    humanizeIconName(IconComponent.displayName) || "icon";
                  const isSelected =
                    React.isValidElement(selectedIcon) &&
                    selectedIcon.type &&
                    selectedIcon.type.displayName === IconComponent.displayName;
                  return (
                    <div style={style} key={index}>
                      {/* Segment-icon-picker (2026-08-18): hover tooltip showing
                          the icon's human name, using the app's standard MUI
                          Tooltip (not the native `title` attr) + the same
                          `humanizeIconName` label the Search matches on. The
                          button keeps its aria-label so screen readers are
                          unaffected. */}
                      <Tooltip title={label} arrow disableInteractive>
                      <button
                        type="button"
                        aria-label={label}
                        aria-pressed={isSelected}
                        onClick={() => handleIconSelect(IconComponent)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "86%",
                          aspectRatio: "1 / 1",
                          maxWidth: 38,
                          maxHeight: 38,
                          margin: "6px auto 0",
                          padding: 0,
                          background: "transparent",
                          border: isSelected
                            ? `1.5px solid ${BRAND_PURPLE}`
                            : "1.5px solid transparent",
                          borderRadius: 6,
                          color: "inherit",
                          cursor: "pointer",
                          boxSizing: "border-box",
                        }}
                      >
                        <IconComponent
                          size={25}
                          style={{ display: "block" }}
                        />
                      </button>
                      </Tooltip>
                    </div>
                  );
                }}
              </Grid>
            )}
          </AutoSizer>
        )}
      </Box>
    </Box>
  );

  if (showAccordion) {
    return (
      <CustomAccordion
        title={title}
        // Icon-picker-expand (2026-08-20, ISP-9999): the preview shows the
        // CHOSEN icon regardless of the hide toggle's state — toggling off is
        // enabled/disabled only, never a clear action.
        // T22.154 (Kelly 2026-10-12): the ⊘ BlockIcon swatch read as "disabled"
        // and nobody knew it opened the picker. The header now ends in the same
        // brand caret as Segment Fill (ExpandMore, rotated when open); a chosen
        // icon still previews just before it.
        icon={
          <>
            {selectedIcon || null}
            <ExpandMoreIcon
              style={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </>
        }
        expanded={expanded}
        onToggle={setExpanded}
        // Segment-icon-picker (2026-08-18): the (x) HighlightOff clear button is
        // GONE. In its place, a show/hide toggle that PRESERVES the stored icon —
        // checked = shown (hidden === false). stopPropagation so flipping it does
        // not also expand/collapse the accordion. Only rendered when the caller
        // wires the toggle (SegmentSettings); other callers keep no header control.
        secondaryIcon={
          onToggleHidden ? (
            <Switch
              size="small"
              checked={!hidden}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                // T22.154: switching ON opens the picker below; OFF collapses it.
                setExpanded(e.target.checked);
                onToggleHidden(!e.target.checked);
              }}
              inputProps={{ "aria-label": "Show icon on segment" }}
            />
          ) : null
        }
      >
        {content}
      </CustomAccordion>
    );
  } else {
    return <Box>{content}</Box>;
  }
};

SegmentIcon.propTypes = {
  // Optional: a null/absent value is a valid "No icon" state (the picker
  // treats null/""/undefined as none), so this is not required.
  selectedIcon: PropTypes.element,
  onIconSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
  showAccordion: PropTypes.bool,
  hidden: PropTypes.bool,
  onToggleHidden: PropTypes.func,
};

export default SegmentIcon;
